// ─── Client-side image compression ──────────────────────────
// Admin uploads (event photos, service heroes, hero slides, brand logos) used to
// go into Supabase Storage at full camera resolution. A single 6000px JPEG off a
// DSLR is often 8-15 MB, which is slow to upload, slow to serve and eats the
// storage quota. Every image is resized and re-encoded to WebP in the browser
// before it is sent.
//
// Quality notes — this is tuned for AV / live-event photography, which is full of
// coloured stage lighting, haze, beams and dark gradients. That content bands and
// smears badly at low WebP quality, so quality is kept high (0.90) and large
// images are downscaled in HALVING STEPS rather than one big jump, because a
// single large canvas downscale produces soft, blurry output.
//
// Deliberately NOT converted:
//   • SVG  — vector; rasterising it would destroy it (brand logos are often SVG)
//   • GIF  — canvas only captures the first frame, so animation would be lost
//   • non-images (PDFs, docs) — passed straight through
//
// If anything fails, or the "compressed" result is not actually smaller, the
// ORIGINAL file is returned. Compression must never block an upload.

export const MAX_DIMENSION = 2560;
export const WEBP_QUALITY = 0.90;

const PASS_THROUGH = ["image/svg+xml", "image/gif"];

/** Swap any file extension for .webp, keeping the original stem. */
const toWebpName = (name: string): string => {
  const stem = name.replace(/\.[^.]+$/, "");
  return `${stem || "image"}.webp`;
};

/** Load a File into an HTMLImageElement via an object URL. */
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not decode image")); };
    img.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise(resolve => canvas.toBlob(resolve, type, quality));

const makeCanvas = (w: number, h: number): HTMLCanvasElement => {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return c;
};

const drawHighQuality = (ctx: CanvasRenderingContext2D, src: CanvasImageSource, w: number, h: number) => {
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, w, h);
};

/**
 * Downscale to a target size in HALVING STEPS.
 * A single drawImage from e.g. 6000px to 2560px looks soft because the browser
 * samples too sparsely. Halving repeatedly (6000 → 3000 → 2560) keeps edges and
 * fine detail crisp. No-ops when the image is already within bounds.
 */
const steppedDownscale = (img: HTMLImageElement, targetW: number, targetH: number): HTMLCanvasElement => {
  let curW = img.naturalWidth;
  let curH = img.naturalHeight;
  let canvas = makeCanvas(curW, curH);
  let ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  // Halve until the next halving would overshoot the target, then final draw.
  while (curW / 2 > targetW && curH / 2 > targetH) {
    const nextW = Math.round(curW / 2);
    const nextH = Math.round(curH / 2);
    const next = makeCanvas(nextW, nextH);
    const nctx = next.getContext("2d")!;
    drawHighQuality(nctx, canvas, nextW, nextH);
    canvas = next; ctx = nctx;
    curW = nextW; curH = nextH;
  }

  if (curW === targetW && curH === targetH) return canvas;

  const finalCanvas = makeCanvas(targetW, targetH);
  drawHighQuality(finalCanvas.getContext("2d")!, canvas, targetW, targetH);
  return finalCanvas;
};

/**
 * Resize to fit within MAX_DIMENSION and re-encode as WebP.
 * Returns the original File untouched if conversion isn't applicable or isn't a win.
 */
export const compressImage = async (file: File): Promise<File> => {
  try {
    if (!file.type.startsWith("image/")) return file;
    if (PASS_THROUGH.includes(file.type)) return file;

    const img = await loadImage(file);
    const { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return file;

    // Only ever scale DOWN — never upscale a small image into a blurry big one.
    const scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
    const targetW = Math.max(1, Math.round(w * scale));
    const targetH = Math.max(1, Math.round(h * scale));

    // Transparency (PNG logos) is preserved: no background fill, WebP keeps alpha.
    const canvas = scale < 1
      ? steppedDownscale(img, targetW, targetH)
      : (() => { const c = makeCanvas(targetW, targetH); drawHighQuality(c.getContext("2d")!, img, targetW, targetH); return c; })();

    const blob = await canvasToBlob(canvas, "image/webp", WEBP_QUALITY);
    // Browsers without WebP encoding silently hand back a PNG — don't ship that,
    // it would usually be larger than the original.
    if (!blob || blob.type !== "image/webp") return file;

    // If the original was already smaller (common for tiny optimised logos),
    // keep the original.
    if (blob.size >= file.size) return file;

    return new File([blob], toWebpName(file.name), {
      type: "image/webp",
      lastModified: Date.now(),
    });
  } catch {
    // Never let compression break an upload.
    return file;
  }
};
